import { computed, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
// The existing auth store imports the router, which imports the app layout.
// eslint-disable-next-line import/no-cycle
import { useAuthStore } from '@/stores/auth';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';
import { dismissProductTour } from '@/services/productTourService';
import { getProductTourSteps } from '@/constants/productTour';

type TourPhase = 'closed' | 'welcome' | 'loading' | 'tour';

// eslint-disable-next-line import/prefer-default-export
export const useProductTourStore = defineStore('productTour', () => {
    const auth = useAuthStore();
    const creditorAccess = useSuperAdminCreditorAccessStore();
    const phase = shallowRef<TourPhase>('closed');
    const stepIndex = shallowRef(0);
    const dashboardReady = shallowRef(false);
    const replayRequested = shallowRef(false);
    const source = shallowRef<'automatic' | 'replay'>('automatic');
    const offeredThisSession = shallowRef(false);
    const persistenceError = shallowRef(false);
    let sessionVersion = 0;

    const eligible = computed(() => (
        auth.isAuthenticated && auth.can('dashboard')
        && auth.can('payments') && auth.can('reports')
        && (auth.roleName !== 'superadmin' || creditorAccess.isActive)
    ));
    const steps = computed(() => getProductTourSteps(auth.roleName));
    const currentStep = computed(() => steps.value[stepIndex.value]);
    const isOpen = computed(() => phase.value !== 'closed');
    const shouldOfferWelcome = computed(() => (
        // Creditor access is replay-only: never auto-offer the tour over someone else's portal.
        eligible.value && !creditorAccess.isActive && !isOpen.value && !replayRequested.value
        && !offeredThisSession.value && auth.user?.should_show_product_tour === true
    ));

    watch(() => auth.user?.id, () => {
        sessionVersion += 1;
        phase.value = 'closed';
        stepIndex.value = 0;
        replayRequested.value = false;
        offeredThisSession.value = false;
        persistenceError.value = false;
    }, { flush: 'sync' });

    watch(eligible, (allowed) => {
        if (!allowed) {
            phase.value = 'closed';
            replayRequested.value = false;
        }
    }, { flush: 'sync' });

    // The welcome modal never waits for the dashboard, so the first step can be
    // requested before its anchors exist. Release the spotlight once they do.
    watch(dashboardReady, (ready) => {
        if (ready && phase.value === 'loading') phase.value = 'tour';
    }, { flush: 'sync' });

    function offerWelcome(): void {
        if (!shouldOfferWelcome.value) return;
        source.value = 'automatic';
        offeredThisSession.value = true;
        phase.value = 'welcome';
    }

    function start(): void {
        if (!eligible.value) return;
        stepIndex.value = 0;
        phase.value = dashboardReady.value ? 'tour' : 'loading';
    }

    function requestReplay(): void {
        if (!eligible.value) return;
        source.value = 'replay';
        offeredThisSession.value = true;
        replayRequested.value = true;
        phase.value = 'closed';
        stepIndex.value = 0;
    }

    function startReplay(): void {
        if (!replayRequested.value || !eligible.value) return;
        replayRequested.value = false;
        start();
    }

    async function dismiss(): Promise<void> {
        if (!isOpen.value) return;
        phase.value = 'closed';
        replayRequested.value = false;
        // Replay is entirely local, including its dismissal (backend developer's contract).
        // Creditor access must never persist the flag against the impersonated account.
        if (source.value === 'replay' || !eligible.value || creditorAccess.isActive) return;
        const version = sessionVersion;
        try {
            await dismissProductTour();
            if (version === sessionVersion && auth.user) {
                auth.user.should_show_product_tour = false;
            }
        } catch {
            // Closing must never wait for the network. Do not claim persistence succeeded.
            if (version === sessionVersion) persistenceError.value = true;
        }
    }

    function next(): void {
        if (phase.value !== 'tour') return;
        if (stepIndex.value + 1 >= steps.value.length) {
            dismiss();
            return;
        }
        stepIndex.value += 1;
    }

    function cancelReplay(): void {
        replayRequested.value = false;
    }

    return {
        phase,
        stepIndex,
        dashboardReady,
        replayRequested,
        persistenceError,
        eligible,
        steps,
        currentStep,
        isOpen,
        shouldOfferWelcome,
        offerWelcome,
        start,
        requestReplay,
        startReplay,
        cancelReplay,
        dismiss,
        next,
    };
});
